import * as contentful from 'contentful';
import { doc } from 'oasis-os-react';
import { getParam, storage } from 'oasis-os-utils';
import { UrlQueryParams } from './ContentfulProvider';
const getConfigFromRootApp = () => {
    const rootConfig = doc()?.config;
    if (!rootConfig?.CONTENTFUL_TOKEN || !rootConfig?.CONTENTFUL_SPACE) {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('No root config was found. Assuming we are in standalone mode.');
        }
        return;
    }
    return {
        CONTENTFUL_TOKEN: rootConfig.CONTENTFUL_TOKEN,
        CONTENTFUL_SPACE: rootConfig.CONTENTFUL_SPACE,
        CONTENTFUL_ENVIRONMENT: rootConfig.CONTENTFUL_ENVIRONMENT,
    };
};
const getPreviewConfig = () => {
    if (process.env.NODE_ENV !== 'production') {
        console.debug('Preview mode enabled.');
    }
    const previewSpaceId = getParam(UrlQueryParams.SPACE_ID);
    const previewEnvironment = getParam(UrlQueryParams.ENVIRONMENT);
    if (!previewSpaceId || !previewEnvironment) {
        throw Error('Missing environment or spaceId URL param while in Preview mode.');
    }
    const previewContentfulToken = storage.get(`preview-${previewSpaceId}-${previewEnvironment}`) ??
        prompt('Please provide a valid preview token', '');
    storage.set(`preview-${previewSpaceId}-${previewEnvironment}`, previewContentfulToken);
    return {
        CONTENTFUL_ENVIRONMENT: previewEnvironment,
        CONTENTFUL_TOKEN: previewContentfulToken,
        CONTENTFUL_SPACE: previewSpaceId,
    };
};
const reauthenticationPrompt = (contentfulConfig) => (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 404) {
        storage.remove(`preview-${contentfulConfig.CONTENTFUL_SPACE}-${contentfulConfig.CONTENTFUL_ENVIRONMENT}`);
        const newContentPreviewToken = prompt('Invalid Access Token!\nPlease provide a valid preview token', '');
        if (newContentPreviewToken) {
            const newConfig = {
                ...contentfulConfig,
                CONTENTFUL_TOKEN: newContentPreviewToken,
            };
            createContentfulClient(newConfig, true);
            storage.set(`preview-${newConfig.CONTENTFUL_SPACE}-${newConfig.CONTENTFUL_ENVIRONMENT}`, newConfig.CONTENTFUL_TOKEN);
            location.reload();
            return Promise.resolve();
        }
    }
    return Promise.reject(error);
};
function createContentfulClient(contentfulConfig, previewMode) {
    if (!contentfulConfig.CONTENTFUL_TOKEN ||
        !contentfulConfig.CONTENTFUL_SPACE) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('Contentful tokens are missing. This is OK if the app does not use Contentful.', 'If you intended to use Contentful, make sure you add CONTENTFUL_TOKEN and CONTENTFUL_SPACE as environment variable.');
        }
        return;
    }
    return contentful.createClient({
        space: contentfulConfig.CONTENTFUL_SPACE,
        accessToken: contentfulConfig.CONTENTFUL_TOKEN,
        host: previewMode ? 'preview.contentful.com' : 'cdn.contentful.com',
        environment: contentfulConfig.CONTENTFUL_ENVIRONMENT ?? 'master',
        // @ts-ignore Not an official attribute of createClient()
        onError: previewMode ? reauthenticationPrompt(contentfulConfig) : undefined,
    });
}
/** Create an instance of the contentful SDK client with configuration taken from ENV. */
export function createClient(config) {
    let contentfulConfig = getConfigFromRootApp() || {
        CONTENTFUL_TOKEN: config?.token || process.env.CONTENTFUL_TOKEN,
        CONTENTFUL_SPACE: config?.spaceId || process.env.CONTENTFUL_SPACE,
        CONTENTFUL_ENVIRONMENT: config?.environment || process.env.CONTENTFUL_ENVIRONMENT,
    };
    const previewMode = getParam(UrlQueryParams.PREVIEW_MODE) === 'true';
    if (previewMode) {
        contentfulConfig = getPreviewConfig();
    }
    return createContentfulClient(contentfulConfig, previewMode);
}
